import { withTranslation } from "react-i18next";
import { Box, TextField, Typography } from "@mui/material";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import { useEffect, useState } from "react";
import BaseConfigModal from "@plannel/components/modal/BaseConfigModal";
import measureService from "@plannel/services/data/measure-service";
import p13nService from "@plannel/services/system/p13n-service";
import debug from "@plannel/utils/debug-util";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import { cloneDeep, isEqual } from "lodash";
import Snackbar from "@plannel/components/Snackbar";
import ColorUtil from "@plannel/pages/utils/color-util";
import { FS } from "@plannel/utils/font-util";

// 우선순위 빌더(MP Settings Demand Priority)와 동일한 팔레트
const ACC = "#048BA8";
const NEUTRAL_BORDER = "#E0E0E0";
const POOL_BG = "#F1F4F8";

const MeasureLayoutModal = (props) => {
  const { t, close, open, data, modalResultData, allMeasures = null } = props;

  const cloneData = cloneDeep(data);
  const appliedLayout = { ...cloneData.layoutModalData };
  const { warning } = ColorUtil.snackBarMessageColor;
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const [rawMeasureData, setRawMeasureData] = useState([]);
  const [applied, setApplied] = useState([]); // 적용(순서 = 우선순위)
  const [layoutData, setLayoutData] = useState({});
  const [search, setSearch] = useState("");
  const [dndOver, setDndOver] = useState(null); // "pool" | 행 index

  const label = (m) => GridUtils.gridValueL10N(m.displayName ?? m.name);

  const handleClose = () => {
    setSnackInfo({ open: false, severity: "", content: "" });
    close(false);
  };

  const handleConfirm = () => {
    const finalAppliedData = {};
    let seq = 1;
    let isChanged = false;

    applied.forEach((m) => {
      finalAppliedData[m.measureCd] = { ...m, seqNum: seq };
      seq++;
    });

    appliedLayout.activeMeasures = finalAppliedData;
    layoutData[appliedLayout.layoutId] = { ...layoutData[appliedLayout.layoutId], ...appliedLayout };

    if (!isEqual(finalAppliedData, cloneData?.layoutModalData?.activeMeasures)) {
      isChanged = true;
    }

    if (isChanged) {
      modalResultData({ ...appliedLayout, reload: false });
      close(false);
    } else {
      setSnackInfo({ open: true, severity: warning, content: t("MSG_NoChangeData") });
    }
  };

  const sortListByColumn = (list, columnName) => {
    list.sort((a, b) => {
      if (a[columnName] > b[columnName]) return 1;
      if (a[columnName] < b[columnName]) return -1;
      return 0;
    });
    return list;
  };

  const retrieveMeasures = () => {
    measureService.getAll({ isActive: true })
      .then((response) => {
        const result = response.data.results;
        for (const row of result) {
          row.userMeasure = row.measureCd.includes("USER_MEASURE") ? true : false;
        }
        let sortList = sortListByColumn(result, "name");
        sortList = sortListByColumn(sortList, "userMeasure");
        setRawMeasureData(sortList);
      })
      .catch((e) => {
        debug(e);
      });
  };

  const retrieveP13n = () => {
    p13nService.getLayout(data.viewName)
      .then((response) => {
        const result = response.data;
        const layoutDataAll = {};
        for (const layout of result) {
          layoutDataAll[layout.layoutId] = layout;
        }
        setLayoutData(layoutDataAll);
      })
      .catch((e) => {
        debug(e);
      });
  };

  // 원천 로드 시 적용 목록 초기화 (seqNum 순)
  //  rawMeasureData 에서 같은 measureCd 를 찾아 최신값을 쓴다.
  // rawMeasureData 에 더 이상 없는(삭제/비활성) measureCd 는 목록에서 뺀다.
  useEffect(() => {
    if (!rawMeasureData.length) return;
    const appliedMeasure = appliedLayout.activeMeasures;
    if (appliedMeasure) {
      const ordered = Object.values(appliedMeasure)
        .sort((a, b) => (a?.seqNum < b?.seqNum ? -1 : 1))
        .map((saved) => {
          const fresh = rawMeasureData.find((m) => m.measureCd === saved.measureCd);
          return fresh ? { ...fresh, seqNum: saved.seqNum } : null;
        })
        .filter(Boolean);
      setApplied(ordered);
    } else {
      setApplied([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawMeasureData]);

  useEffect(() => {
    if (open) {
      setSearch("");
      retrieveP13n();
      if (!allMeasures) {
        retrieveMeasures();
      } else {
        setRawMeasureData(allMeasures);
      }
    } else {
      setSnackInfo({ open: false, severity: "", content: "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ── pool / applied ──
  const isApplied = (cd) => applied.some((m) => m.measureCd === cd);
  const measureByCd = (cd) => rawMeasureData.find((m) => m.measureCd === cd);
  const poolItems = rawMeasureData.filter(
    (m) => !isApplied(m.measureCd) && (!search || label(m).toLowerCase().includes(search.toLowerCase()))
  );

  // ── 드래그&드롭 + 클릭 ──
  const dragStart = (cd) => (e) => {
    e.dataTransfer.setData("text/plain", cd);
    e.dataTransfer.effectAllowed = "move";
  };
  const addAt = (cd, idx) => setApplied((prev) => {
    const item = prev.find((m) => m.measureCd === cd) || measureByCd(cd);
    if (!item) return prev;
    const rest = prev.filter((m) => m.measureCd !== cd);
    const at = idx == null ? rest.length : Math.max(0, Math.min(idx, rest.length));
    rest.splice(at, 0, item);
    return rest;
  });
  const reorder = (cd, idx) => setApplied((prev) => {
    const cur = prev.findIndex((m) => m.measureCd === cd);
    if (cur < 0) return prev;
    const item = prev[cur];
    const rest = prev.filter((m) => m.measureCd !== cd);
    let at = idx == null ? rest.length : (idx > cur ? idx - 1 : idx);
    at = Math.max(0, Math.min(at, rest.length));
    rest.splice(at, 0, item);
    return rest;
  });
  const dropAt = (idx) => (e) => {
    e.preventDefault();
    setDndOver(null);
    const cd = e.dataTransfer.getData("text/plain");
    if (!cd) return;
    if (isApplied(cd)) reorder(cd, idx); else addAt(cd, idx);
  };
  const removeApplied = (cd) => setApplied((prev) => prev.filter((m) => m.measureCd !== cd));
  const dropOnPool = (e) => {
    e.preventDefault();
    setDndOver(null);
    const cd = e.dataTransfer.getData("text/plain");
    if (cd && isApplied(cd)) removeApplied(cd);
  };

  const poolChipSx = {
    display: "inline-flex", alignItems: "center", gap: 0.4,
    border: `1px solid ${NEUTRAL_BORDER}`, borderRadius: "7px", px: "8px", py: "3px",
    fontSize: FS.SUB, fontWeight: 700, color: "text.secondary", bgcolor: "#fff", cursor: "pointer",
    maxWidth: "100%", transition: "background-color .12s, box-shadow .12s, border-color .12s",
    "&:hover": { bgcolor: `${ACC}0D`, borderColor: ACC, boxShadow: `0 0 0 1px ${ACC}` },
  };

  return (
    /* handleConfirm 은 modalResultData 로 부모에 넘기고 닫을 뿐 저장하지 않으므로 confirm 유지. */
    <BaseConfigModal
      open={open}
      onClose={handleClose}
      title={t("manageMeasureLayout")}
      onSave={handleConfirm}
      saveLabel={t("confirm")}
      saveIcon={<DoneAllIcon />}
      width={900}
    >
        {/* 본문 — 좌: 후보 풀(검색) / 우: 적용(우선순위 순).
            셸 안에서는 높이를 물려받을 부모가 없어 뷰포트 기준으로 직접 정한다(예전 62%/minHeight 440 대체). */}
        <Box sx={{ height: "56vh", minHeight: 380, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>

          {/* 좌: 후보 — 드롭 시 적용 해제 */}
          <Box
            onDragOver={(e) => { e.preventDefault(); setDndOver("pool"); }}
            onDragLeave={() => setDndOver((p) => (p === "pool" ? null : p))}
            onDrop={dropOnPool}
            sx={{
              display: "flex", flexDirection: "column", minHeight: 0, boxSizing: "border-box",
              borderRadius: "8px", p: 1.25,
              border: `1px ${dndOver === "pool" ? "dashed" : "solid"} ${dndOver === "pool" ? ACC : NEUTRAL_BORDER}`,
              bgcolor: dndOver === "pool" ? `${ACC}0D` : POOL_BG,
            }}>
            <Typography sx={{ fontSize: FS.BODY, fontWeight: 700, color: "text.secondary", mb: 0.75 }}>
              {t("selectableMeasure")}
            </Typography>
            <TextField
              size="small"
              fullWidth
              placeholder={t("search")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ mb: 1, bgcolor: "#fff", borderRadius: "6px", "& .MuiInputBase-input": { fontSize: FS.SUB, py: "6px" } }}
            />
            <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", display: "flex", flexWrap: "wrap", gap: 0.75, alignContent: "flex-start" }}>
              {poolItems.map((m) => (
                <Box
                  key={m.measureCd}
                  draggable
                  onDragStart={dragStart(m.measureCd)}
                  onClick={() => addAt(m.measureCd, null)}
                  sx={poolChipSx}>
                  <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {label(m)}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          {/* 우: 적용(우선순위 순) — 말미 추가 드롭 + 행별 삽입 드롭 */}
          <Box
            onDragOver={(e) => e.preventDefault()}
            onDrop={dropAt(applied.length)}
            sx={{
              display: "flex", flexDirection: "column", minHeight: 0, boxSizing: "border-box",
              borderRadius: "8px", p: 1.25, bgcolor: "#fff",
              border: `1px solid ${applied.length ? `${ACC}55` : NEUTRAL_BORDER}`,
            }}>
            <Typography sx={{ fontSize: FS.BODY, fontWeight: 700, color: applied.length ? ACC : "text.secondary", mb: 0.75 }}>
              {t("appliedMeasure")} ({applied.length})
            </Typography>
            <Box sx={{ flex: 1, minHeight: 0, overflowY: applied.length ? "auto" : "hidden" }}>
              {applied.length === 0 ? (
                <Box sx={{ borderRadius: "6px", border: `1px dashed ${NEUTRAL_BORDER}`, height: "100%", minHeight: 64, boxSizing: "border-box" }} />
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                  {applied.map((m, i) => (
                    <Box
                      key={m.measureCd}
                      draggable
                      onDragStart={dragStart(m.measureCd)}
                      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDndOver(i); }}
                      onDragLeave={() => setDndOver((p) => (p === i ? null : p))}
                      onDrop={(e) => { e.stopPropagation(); dropAt(i)(e); }}
                      sx={{
                        display: "flex", alignItems: "center", gap: 0.5, width: "100%", boxSizing: "border-box",
                        border: `1px solid ${ACC}`, borderRadius: "7px", px: "8px", py: "5px",
                        bgcolor: `${ACC}14`, color: "text.primary", fontSize: FS.SUB, fontWeight: 700, cursor: "grab",
                        boxShadow: dndOver === i ? `inset 0 2px 0 0 ${ACC}` : "none",
                        transition: "background-color .12s, box-shadow .12s",
                        "&:hover": { bgcolor: `${ACC}2E`, boxShadow: dndOver === i ? `inset 0 2px 0 0 ${ACC}` : `0 0 0 1px ${ACC}` },
                      }}>
                      <Box component="span" sx={{
                        minWidth: 18, height: 18, px: "3px", borderRadius: "50%", bgcolor: ACC, color: "#fff",
                        fontSize: FS.CAPTION, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                      }}>
                        {i + 1}
                      </Box>
                      <Box component="span" sx={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {label(m)}
                      </Box>
                      <Box
                        component="button"
                        type="button"
                        onClick={() => removeApplied(m.measureCd)}
                        sx={{
                          minWidth: 18, height: 18, px: "2px", border: "none", borderRadius: "4px",
                          bgcolor: `${ACC}14`, color: ACC, fontSize: FS.BODY, lineHeight: 1, cursor: "pointer", ml: 0.2,
                          display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                          "&:hover": { bgcolor: `${ACC}26` },
                        }}>
                        ✕
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
        <Snackbar
          open={snackInfo.open}
          onClose={() => setSnackInfo({ ...snackInfo, open: false })}
          severity={snackInfo.severity}
          content={snackInfo.content}
        />
    </BaseConfigModal>
  );
};

export default withTranslation()(MeasureLayoutModal);
